// Single source of truth for config-driven scouting behaviour. The ONLY place
// the stored measurement term ('percentage') and the spec/UI term ('incidence')
// meet — see RECONCILIATION.md §4.2 (D3 revised). No DB rename; map at boundary.

export type StoredMeasurementType = 'percentage' | 'level' | 'count';
export type SpecMeasurementType = 'incidence' | 'level' | 'count';
export type SamplingUnit = 'point' | 'tree';
export type TallyThreshold = 'any' | '2+' | '3+';
export type Category = 'pest' | 'disease' | 'beneficial' | 'weed' | 'disorder';

/** Stored term (or undefined) → spec/UI term. 'percentage' and undefined → 'incidence'. */
export function normalizeMeasurementType(stored?: string | null): SpecMeasurementType {
  if (stored === 'level') return 'level';
  if (stored === 'count') return 'count';
  return 'incidence'; // 'percentage', '', null, undefined, or any legacy value
}

/** Spec/UI term → stored term. 'incidence' → 'percentage'. */
export function toStoredMeasurementType(spec: SpecMeasurementType): StoredMeasurementType {
  if (spec === 'level') return 'level';
  if (spec === 'count') return 'count';
  return 'percentage';
}

/** Sampling unit + tally threshold apply to incidence pests only (Impl Spec §4.2). */
export function showsSamplingAndTally(spec: SpecMeasurementType): boolean {
  return spec === 'incidence';
}

/** Default action threshold per type, applied on type switch (Impl Spec §4.2). */
export function typeDefaultThreshold(spec: SpecMeasurementType): number {
  switch (spec) {
    case 'incidence': return 20;
    case 'level': return 3;
    case 'count': return 12;
    default: { const _exhaustive: never = spec; return _exhaustive; }
  }
}

export interface ActionThresholdDescriptor {
  /** i18n key for the unit suffix label. */
  unitLabelKey: string;
  min: number;
  max: number;
  step: number;
  render: (value: number) => string;
}

/** Stepper bounds + render per type (Impl Spec §4.3). */
export function actionThresholdDescriptor(spec: SpecMeasurementType): ActionThresholdDescriptor {
  switch (spec) {
    case 'level':
      return { unitLabelKey: 'fieldScout.config.threshold.unit.level', min: 0, max: 4, step: 1, render: v => `${v}/4` };
    case 'count':
      return { unitLabelKey: 'fieldScout.config.threshold.unit.count', min: 0, max: 999, step: 1, render: v => `${v}` };
    case 'incidence':
      return { unitLabelKey: 'fieldScout.config.threshold.unit.incidence', min: 0, max: 100, step: 5, render: v => `${v}%` };
    default: { const _exhaustive: never = spec; return _exhaustive; }
  }
}

/** Type colour key: incidence=blue, level=purple, count=green. */
export const TYPE_COLOR_KEY: Record<SpecMeasurementType, 'inc' | 'lev' | 'cnt'> = {
  incidence: 'inc',
  level: 'lev',
  count: 'cnt',
};

export const SPEC_TYPES: SpecMeasurementType[] = ['incidence', 'level', 'count'];
export const SAMPLING_UNITS: SamplingUnit[] = ['point', 'tree'];
export const TALLY_THRESHOLDS: TallyThreshold[] = ['any', '2+', '3+'];
export const CATEGORIES: Category[] = ['pest', 'disease', 'beneficial', 'weed', 'disorder'];
